import { useState, useEffect, useRef } from 'react';
import { X, Loader2, Mail } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Portal } from './Portal';
import { useLanguage } from '../contexts/LanguageContext';
import { log } from '../lib/logger';

interface ChangeEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentEmail: string;
}

export function ChangeEmailDialog({
  open,
  onOpenChange,
  currentEmail,
}: ChangeEmailDialogProps) {
  const { t, translateError } = useLanguage();
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setNewEmail('');
      setError('');
      setSuccess(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onOpenChange]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onOpenChange(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateEmail(newEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      setError('New email must be different from current email');
      return;
    }

    try {
      setLoading(true);

      const { error: updateError } = await supabase.auth.updateUser({
        email: newEmail,
      });

      if (updateError) throw updateError;

      setSuccess(true);
      setTimeout(() => {
        onOpenChange(false);
      }, 3000);
    } catch (err) {
      log.error('Error updating email:', err);
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Portal>
      <div
        data-testid="dialog-backdrop"
        className="fixed inset-0 z-50 bg-gray-900/40 backdrop-blur-sm backdrop-blur-sm"
        onClick={handleBackdropClick}
      />
      <div
        data-testid="dialog-content"
        className="fixed z-50 inset-0 grid place-items-center p-4"
        onClick={handleBackdropClick}
      >
        <div
          ref={dialogRef}
          className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 w-full max-w-md"
        >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 id="change-email-title" className="text-xl font-semibold text-gray-900">
            {t('profile.changeEmail.title')}
          </h2>
          <button
            onClick={() => onOpenChange(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close dialog"
            type="button"
            disabled={loading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('profile.changeEmail.currentEmail')}
            </label>
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">{currentEmail}</span>
            </div>
          </div>

          <div>
            <label htmlFor="new-email" className="block text-sm font-medium text-gray-700 mb-2">
              {t('profile.changeEmail.newEmail')}
            </label>
            <input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder={t('profile.changeEmail.newEmailPlaceholder')}
              required
              disabled={loading}
              aria-required="true"
              aria-invalid={error ? 'true' : 'false'}
            />
          </div>

          {error && (
            <div
              className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
              role="alert"
              aria-live="polite"
            >
              {error}
            </div>
          )}

          {success && (
            <div
              className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700"
              role="alert"
              aria-live="polite"
            >
              {t('profile.changeEmail.success')}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              {t('profile.changeEmail.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:scale-[1.02]"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{loading ? t('profile.changeEmail.changing') : t('profile.changeEmail.change')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
    </Portal>
  );
}
